import { z } from 'zod';

/**
 * Canonical billing plan identifiers used across transport, service, and UI layers.
 */
export const billingPlanIdSchema = z.enum(['free', 'open-source', 'team', 'pro']);
export type BillingPlanId = z.infer<typeof billingPlanIdSchema>;

/**
 * Feature tiers. Entitlements and feature gates check the tier, never the plan,
 * so a Max subscription passes every Pro check.
 */
export const paidPlanTierSchema = z.enum(['team', 'pro']);
export type PaidPlanTier = z.infer<typeof paidPlanTierSchema>;

export function isPaidPlanTier(value: string | null | undefined): value is PaidPlanTier {
  return paidPlanTierSchema.safeParse(value).success;
}

/**
 * Paid plans that can be selected in checkout / plan-change flows, cheapest first.
 */
export const paidPlanSchema = z.enum(['team', 'pro', 'max']);
export type PaidPlan = z.infer<typeof paidPlanSchema>;

export function isPaidPlan(value: string | null | undefined): value is PaidPlan {
  return paidPlanSchema.safeParse(value).success;
}

export const PAID_PLANS = {
  team: { tier: 'team', name: 'Team' },
  pro: { tier: 'pro', name: 'Pro' },
  max: { tier: 'pro', name: 'Max' },
} as const satisfies Record<PaidPlan, { tier: PaidPlanTier; name: string }>;

/**
 * Max includes this multiple of Pro's per-seat usage allowances: reviewed lines
 * and Auto Fix fixes.
 */
export const MAX_PLAN_USAGE_MULTIPLIER = 2;

export function isPaidPlanUpgrade(from: PaidPlan, to: PaidPlan): boolean {
  return paidPlanSchema.options.indexOf(to) > paidPlanSchema.options.indexOf(from);
}

/** Pro-tier plans get every Pro feature and the activity-based Pro seat minimum. */
export function isProTierPlan(plan: PaidPlan): boolean {
  return PAID_PLANS[plan].tier === 'pro';
}

export function getSeatSelectionMessage(plan: PaidPlan): string {
  return `Select the number of seats for the ${PAID_PLANS[plan].name} plan.`;
}

/**
 * Billing intervals used across checkout, pricing UI, and Stripe price resolution.
 */
export const billingIntervalSchema = z.enum(['monthly', 'yearly']);
export type BillingInterval = z.infer<typeof billingIntervalSchema>;

/** What one seat costs: Stripe's unit amount for the interval it bills, never converted. */
export type SeatPrice = { amountCents: number; interval: 'month' | 'year' };

/**
 * The seat price from a Stripe price's unit amount and its interval; null when either is missing,
 * as with a tiered price. A price set with sub-cent precision has only Stripe's
 * `unit_amount_decimal`, a string of cents, which is rounded to the nearest cent.
 * A price billed every several intervals, such as every 3 months, is also null, since
 * `SeatPrice` can only say per month or per year. A missing interval count means 1.
 */
export function getSeatPrice(
  unitAmount: { cents: number | null; decimal: string | null },
  recurring: { interval: 'month' | 'year' | null; intervalCount: number | null },
): SeatPrice | null {
  const amountCents =
    unitAmount.cents ??
    (unitAmount.decimal?.trim() ? Math.round(Number(unitAmount.decimal)) : null);
  const { interval } = recurring;
  if (amountCents === null || !Number.isFinite(amountCents) || interval === null) {
    return null;
  }
  return (recurring.intervalCount ?? 1) === 1 ? { amountCents, interval } : null;
}

export const PRO_PLAN_CONTACT_EMAIL = 'contact@cubic.dev';
